import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import { FiCheckCircle, FiClock, FiPackage } from 'react-icons/fi';
import PropTypes from 'prop-types';

const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && error.error) return error.error;
  return 'An error occurred';
};

const ShopDrawingAnalysis = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Card className="tw-p-6 tw-border tw-border-gray-200">
        <div className="tw-flex tw-justify-center tw-items-center tw-py-12">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="tw-bg-red-50 tw-border tw-border-red-200 tw-p-6">
        <p className="tw-text-red-600 tw-font-semibold">
          Error loading shop drawing analysis
        </p>
        <p className="tw-text-red-500 tw-text-sm">{getErrorMessage(error)}</p>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="tw-p-6 tw-border tw-border-gray-200">
        <p className="tw-text-gray-500 tw-text-center tw-py-8">
          No shop drawing data available
        </p>
      </Card>
    );
  }

  return (
    <Card className="tw-border tw-border-gray-200">
      <div className="tw-p-6 tw-border-b tw-border-gray-200">
        <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">
          Shop Drawing Analysis
        </h3>
        <p className="tw-text-sm tw-text-gray-600 tw-mt-1">
          Completion status and timeline of shop drawing projects
        </p>
      </div>

      <div className="tw-overflow-x-auto">
        <table className="tw-w-full tw-text-sm">
          <thead>
            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
              <th className="tw-text-left tw-py-3 tw-px-6 tw-font-semibold tw-text-gray-700">
                CSE Name
              </th>
              <th className="tw-text-center tw-py-3 tw-px-6 tw-font-semibold tw-text-gray-700">
                Total Drawings
              </th>
              <th className="tw-text-center tw-py-3 tw-px-6 tw-font-semibold tw-text-gray-700">
                Completed
              </th>
              <th className="tw-text-center tw-py-3 tw-px-6 tw-font-semibold tw-text-gray-700">
                Pending
              </th>
              <th className="tw-text-center tw-py-3 tw-px-6 tw-font-semibold tw-text-gray-700">
                Completion %
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => {
              const completionPercent = row.completion_percentage || 0;
              const getProgressColor = (percent) => {
                if (percent >= 80) return 'tw-bg-green-500';
                if (percent >= 60) return 'tw-bg-blue-500';
                if (percent >= 40) return 'tw-bg-yellow-500';
                return 'tw-bg-red-500';
              };

              return (
                <tr
                  key={index}
                  className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50 tw-transition-colors"
                >
                  <td className="tw-py-4 tw-px-6 tw-font-medium tw-text-gray-900">
                    {row.cse_name}
                  </td>
                  <td className="tw-py-4 tw-px-6 tw-text-center">
                    <div className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1 tw-rounded-xl tw-bg-blue-100 tw-text-blue-700">
                      <FiPackage size={14} />
                      <span className="tw-font-semibold">{row.total_drawings}</span>
                    </div>
                  </td>
                  <td className="tw-py-4 tw-px-6 tw-text-center">
                    <div className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1 tw-rounded-xl tw-bg-green-100 tw-text-green-700">
                      <FiCheckCircle size={14} />
                      <span className="tw-font-semibold">{row.completed}</span>
                    </div>
                  </td>
                  <td className="tw-py-4 tw-px-6 tw-text-center">
                    <div className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1 tw-rounded-xl tw-bg-orange-100 tw-text-orange-700">
                      <FiClock size={14} />
                      <span className="tw-font-semibold">{row.pending}</span>
                    </div>
                  </td>
                  <td className="tw-py-4 tw-px-6">
                    <div className="tw-w-full">
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <span className="tw-font-semibold tw-text-gray-900">
                          {completionPercent}%
                        </span>
                      </div>
                      <div className="tw-w-full tw-h-2 tw-bg-gray-200 tw-rounded-xl tw-overflow-hidden">
                        <div
                          className={`tw-h-full ${getProgressColor(completionPercent)} tw-transition-all tw-duration-300`}
                          style={{ width: `${completionPercent}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

ShopDrawingAnalysis.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      cse_name: PropTypes.string.isRequired,
      total_drawings: PropTypes.number.isRequired,
      completed: PropTypes.number.isRequired,
      pending: PropTypes.number.isRequired,
      completion_percentage: PropTypes.number.isRequired,
    })
  ),
  loading: PropTypes.bool.isRequired,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default ShopDrawingAnalysis;
